import { apiDownload, apiRequest } from './apiClient';
import type { ActivityExportFormat } from '@sparkapp/shared-types';
import type {
  ActivitiesListResponse,
  ActivityDetail,
  ActivityFilterOptions,
  ActivityFilters,
  ActivityPayload,
  ActivityStatus,
} from '../types/activity';
import type { PlayType, PlayTypeFormData, CreatePlayTypePayload } from '../types/play-type';
import type { DocumentResponse, CreateDocumentRequest, MarkUploadedRequest } from '../types/upload';

type Envelope<T> = { success: true; data: T };

export const activityApi = {
  async listActivities(filters: Partial<ActivityFilters>): Promise<ActivitiesListResponse> {
    const params = new URLSearchParams();
    if (filters.search) params.set('search', filters.search);
    if (filters.domainId) params.set('domainId', String(filters.domainId));
    if (filters.skillId) params.set('skillId', String(filters.skillId));
    if (filters.playTypeId) params.set('playTypeId', String(filters.playTypeId));
    if (filters.physicalDeckId) params.set('physicalDeckId', String(filters.physicalDeckId));
    if (filters.preferenceId) params.set('preferenceId', String(filters.preferenceId));
    params.set('status', filters.status ?? 'all');
    return (await apiRequest<Envelope<ActivitiesListResponse>>(`/admin/activities?${params}`)).data;
  },

  async getActivity(id: number): Promise<ActivityDetail> {
    return (await apiRequest<Envelope<ActivityDetail>>(`/admin/activities/${id}`)).data;
  },

  async exportActivities(format: ActivityExportFormat): Promise<void> {
    const date = new Date().toISOString().slice(0, 10);
    await apiDownload(`/v2/admin/activity-export?format=${format}`, `activities-export-${date}.${format}`);
  },

  async getFilterOptions(): Promise<ActivityFilterOptions> {
    return (await apiRequest<Envelope<ActivityFilterOptions>>('/admin/activities/filters')).data;
  },

  async createActivity(payload: ActivityPayload): Promise<ActivityDetail> {
    return (await apiRequest<Envelope<ActivityDetail>>('/admin/activities', { method: 'POST', body: payload })).data;
  },

  async updateActivity(id: number, payload: ActivityPayload): Promise<ActivityDetail> {
    return (await apiRequest<Envelope<ActivityDetail>>(`/admin/activities/${id}`, { method: 'PATCH', body: payload })).data;
  },

  async updateStatus(id: number, status: Exclude<ActivityStatus, 'draft'>) {
    return (
      await apiRequest<Envelope<{ id: number; status: ActivityStatus; allowedTransitions: ActivityStatus[] }>>(
        `/admin/activities/${id}/status`,
        { method: 'PATCH', body: { status } },
      )
    ).data;
  },
};

/** Image upload endpoints for activities (used by ActivityImageUpload) */
export const activityUploadApi = {
  async createDocument(data: CreateDocumentRequest): Promise<DocumentResponse> {
    return (await apiRequest<{ data: DocumentResponse }>('/admin/activities/upload/create', { method: 'POST', body: data })).data;
  },
  async markAsUploaded(data: MarkUploadedRequest): Promise<void> {
    await apiRequest<void>('/admin/activities/upload/mark-uploaded', { method: 'POST', body: data });
  },
};

export const playTypeApi = {
  async listPlayTypes(): Promise<PlayType[]> {
    return (await apiRequest<Envelope<PlayType[]>>('/admin/play-types')).data;
  },
  async createPlayType(payload: CreatePlayTypePayload): Promise<PlayType> {
    return (await apiRequest<Envelope<PlayType>>('/admin/play-types', { method: 'POST', body: payload })).data;
  },
  async updatePlayType(id: number, payload: PlayTypeFormData): Promise<PlayType> {
    return (await apiRequest<Envelope<PlayType>>(`/admin/play-types/${id}`, { method: 'PATCH', body: payload })).data;
  },
};
